<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useTimeoutFn } from '@vueuse/core';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { INPUT_DEBOUNCE } from '@/modules/common/consts/ui.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { DeadlineType } from '@/modules/common/enums/deadline-type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import type { DateCopy, Deadline, Direction } from '@/modules/common/types/ui.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import UiButtonToggle from '@/modules/ui/components/ui/UiButtonToggle.vue';
import UiDate from '@/modules/ui/components/ui/UiDate.vue';
import UiInputNumber from '@/modules/ui/components/ui/UiInputNumber.vue';

const MIN_DAYS = 1;
const INITIAL_DAYS = 1;
const DAYS_MAX_LENGTH = 4;

const {
  required,
  from = DateTime.now(),
  direction = 'forward',
  today,
  correct = false, // TODO: Отказаться
  controls = true,
  getDeadline,
  getWorkDaysDiff,
} = defineProps<{
  error?: boolean;
  disabled?: boolean;
  from?: DateTime;
  direction?: Direction;
  required?: boolean;
  today?: boolean;
  copy?: DateCopy;
  fullWidth?: boolean;
  correct?: boolean;
  controls?: boolean;
  getDeadline: (parameters: { days: number; from?: DateTime }, signal?: AbortSignal) => Promise<DateTime>;
  getWorkDaysDiff: (parameters: { from: DateTime; to: DateTime }, signal?: AbortSignal) => Promise<number>;
}>();

defineSlots<{
  prefix?: () => unknown;
}>();

const model = defineModel<Deadline>({
  default: (): Deadline => ({
    type: DeadlineType.None,
    days: INITIAL_DAYS,
    date: DateTime.now().plus({ days: INITIAL_DAYS }),
  }),
});

const { abort, signal } = useAbort();
const { abort: abortMaxWorkingDays, signal: maxWorkingDaysSignal } = useAbort();

const switchOptions = computed((): Option<DeadlineType>[] =>
  [
    !required && {
      label: 'ui.deadline.options.none',
      code: DeadlineType.None,
    },
    {
      label: 'ui.deadline.options.workingDays',
      code: DeadlineType.WorkingDays,
    },
    {
      label: 'ui.deadline.options.days',
      code: DeadlineType.Days,
    },
    {
      label: 'ui.date',
      code: DeadlineType.Date,
    },
  ].filter((o) => !!o)
);
const fromDay = computed(() => from.startOf('day'));
const minDate = computed(() => {
  const startOfToday = DateTime.now().startOf('day');
  const base = direction === 'forward' ? fromDay.value : startOfToday;

  return (correct ? base : DateTime.max(base, startOfToday)).plus({ days: today ? 0 : 1 });
});
const maxDate = computed(() => (direction === 'forward' ? undefined : fromDay.value));
const maxCalendarDays = computed(() =>
  direction === 'forward' ? undefined : Math.ceil(fromDay.value.diffNow('day').days) - 1
);
const maxWorkingDays = ref<number>();
const maxDays = computed(() =>
  model.value.type === DeadlineType.WorkingDays
    ? (maxWorkingDays.value ?? maxCalendarDays.value)
    : maxCalendarDays.value
);

const calendarAvailable = computed(
  () => model.value.type === DeadlineType.WorkingDays || model.value.type === DeadlineType.Days
);

const isMaxWorkingDaysExceeded = computed(
  () =>
    model.value.type === DeadlineType.WorkingDays &&
    typeof model.value.days === 'number' &&
    typeof maxWorkingDays.value === 'number' &&
    model.value.days > maxWorkingDays.value
);

function notifyError(error: unknown) {
  if (!ErrorUtil.isUnexpected(error)) {
    return;
  }

  notification.error('ui.common.notification.error.common.getWorkingDays', error);
}

function showCorrectionWarning(date: DateTime) {
  notification.warning({
    key: 'ui.common.notification.warning.common.deadlineAutoChanged',
    params: {
      // TODO: Используйте use-formatted-date
      date: date.toLocaleString(),
    },
  });
}

async function setMaxWorkingDays() {
  if (direction === 'forward') {
    maxWorkingDays.value = undefined;
    return;
  }

  abortMaxWorkingDays('A new value has been received');

  try {
    maxWorkingDays.value = await getWorkDaysDiff(
      {
        from: minDate.value,
        to: fromDay.value,
      },
      maxWorkingDaysSignal.value
    );
  } catch (error) {
    notifyError(error);
  }
}

function normalizeDays() {
  const days = model.value.days;

  if (typeof days !== 'number') {
    return MIN_DAYS;
  }

  if (days < MIN_DAYS) {
    model.value.days = MIN_DAYS;
  }

  return Math.max(MIN_DAYS, days);
}

async function setDateByWorkingDays(showWarning: boolean, expectedType: DeadlineType = DeadlineType.WorkingDays) {
  const days = normalizeDays();

  abort('A new value has been received');

  const requestSignal = signal.value;

  try {
    const date = await getDeadline(
      {
        days: direction === 'forward' ? days : -days,
        from,
      },
      requestSignal
    );

    if (requestSignal.aborted || model.value.type !== expectedType) {
      return;
    }

    model.value.date = date;

    if (showWarning) {
      showCorrectionWarning(date);
    }
  } catch (error) {
    notifyError(error);
  }
}

function setDateByDays(showWarning: boolean) {
  const days = normalizeDays();

  abort('A new value has been received');

  model.value.date = fromDay.value.plus({ days: direction === 'forward' ? days : -days });

  if (showWarning) {
    showCorrectionWarning(model.value.date);
  }
}

function setDaysByDate(date: DateTime) {
  const diff = date.startOf('day').diff(fromDay.value, 'days').days;
  const rawDays = Math.ceil(direction === 'forward' ? diff : -diff);

  model.value.days = Math.max(MIN_DAYS, rawDays);

  return rawDays < MIN_DAYS;
}

async function setWorkingDaysByDate(date: DateTime) {
  abort('A new value has been received');

  const requestSignal = signal.value;
  const [fromDate, toDate] = direction === 'forward' ? [fromDay.value, date] : [date, fromDay.value];

  try {
    const rawDays = await getWorkDaysDiff(
      {
        from: fromDate,
        to: toDate,
      },
      requestSignal
    );

    if (requestSignal.aborted) {
      return;
    }

    model.value.days = Math.max(MIN_DAYS, rawDays);

    await setDateByWorkingDays(false);
  } catch (error) {
    notifyError(error);
  }
}

const {
  start: scheduleDateByWorkingDays,
  stop: cancelDateByWorkingDays,
  isPending: isDateByWorkingDaysPending,
} = useTimeoutFn(() => setDateByWorkingDays(false), INPUT_DEBOUNCE, { immediate: false });

function correctDate() {
  if (correct) {
    return;
  }

  if (model.value.date < minDate.value) {
    model.value.date = minDate.value;
    showCorrectionWarning(minDate.value);
  } else if (maxDate.value && model.value.date > maxDate.value) {
    model.value.date = maxDate.value;
    showCorrectionWarning(maxDate.value);
  }
}

function onTypeChange(type: DeadlineType) {
  const prevType = model.value.type;
  const hasPendingWorkingDays = prevType === DeadlineType.WorkingDays && isDateByWorkingDaysPending.value;

  cancelDateByWorkingDays();
  abort('The type has changed');
  model.value.type = type;

  switch (type) {
    case DeadlineType.WorkingDays:
      if (prevType === DeadlineType.Date) {
        void setWorkingDaysByDate(model.value.date);
      } else {
        void setDateByWorkingDays(false);
      }
      break;
    case DeadlineType.Days:
      if (prevType !== DeadlineType.Date || setDaysByDate(model.value.date)) {
        setDateByDays(false);
      }
      break;
    case DeadlineType.Date:
      if (hasPendingWorkingDays) {
        void setDateByWorkingDays(false, DeadlineType.Date).then(correctDate);
      } else {
        correctDate();
      }
      break;
  }
}

function onDaysChange(days: number | null) {
  cancelDateByWorkingDays();

  model.value.days = days as number;

  switch (model.value.type) {
    case DeadlineType.WorkingDays:
      scheduleDateByWorkingDays();
      break;
    case DeadlineType.Days:
      setDateByDays(false);
      break;
  }
}

function onPickerChange(date: DateTime) {
  cancelDateByWorkingDays();

  switch (model.value.type) {
    case DeadlineType.WorkingDays:
      void setWorkingDaysByDate(date);
      break;
    case DeadlineType.Days:
      if (setDaysByDate(date)) {
        setDateByDays(false);
      }
      break;
  }
}

watch(
  () => [direction, fromDay.value.toMillis()] as const,
  (next, prev) => {
    if (prev?.every((value, index) => value === next[index])) {
      return;
    }

    cancelDateByWorkingDays();
    void setMaxWorkingDays();

    const showWarning = !!prev;

    switch (model.value.type) {
      case DeadlineType.WorkingDays:
        void setDateByWorkingDays(showWarning);
        break;
      case DeadlineType.Days:
        setDateByDays(showWarning);
        break;
      case DeadlineType.Date:
        correctDate();
        break;
    }
  },
  { immediate: true }
);

watch(
  () => [model.value.type, model.value.days, maxWorkingDays.value] as const,
  () => {
    if (!isMaxWorkingDaysExceeded.value) {
      return;
    }

    model.value.days = Math.max(MIN_DAYS, maxWorkingDays.value!);
    void setDateByWorkingDays(false);
  }
);
</script>

<template>
  <div class="ui-deadline">
    <div class="switches">
      <slot name="prefix" />

      <UiButtonToggle
        v-if="controls"
        :model-value="model.type"
        :options="switchOptions"
        :disabled="disabled"
        :full-width="fullWidth"
        @update:model-value="onTypeChange($event as DeadlineType)"
      />
    </div>

    <Transition name="fields">
      <div
        v-if="controls && model.type !== DeadlineType.None"
        class="fields"
      >
        <UiInputNumber
          v-if="model.type !== DeadlineType.Date"
          :model-value="model.days"
          class="item"
          :min="MIN_DAYS"
          :max="maxDays"
          :max-length="DAYS_MAX_LENGTH"
          :disabled="disabled"
          :error
          required
          @update:model-value="onDaysChange"
        />

        <UiDate
          v-model="model.date"
          class="item _date"
          :elements="{
            input: true,
            today,
            copy: model.type === DeadlineType.Date,
            calendar: true,
          }"
          :readonly="model.type !== DeadlineType.Date && 'formatted'"
          :min="minDate"
          :max="maxDate"
          :copy="copy"
          :disabled="disabled"
          :error
          :calendar-available="calendarAvailable"
          required
          @picker-change="onPickerChange($event)"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.ui-deadline {
  display: flex;
  flex-direction: column;
}

.switches {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fields {
  display: flex;
  height: 44px;
  align-items: flex-end;
  gap: 4px;

  &-enter-from,
  &-leave-to {
    height: 0;
    opacity: 0;
  }

  &-enter-active,
  &-leave-active {
    transition:
      height var(--transition),
      opacity var(--transition);
  }
}

.item {
  flex: 1 1 50%;
  max-width: 310px;

  &._date {
    min-width: 175px;
  }
}
</style>
